import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Eye, Power } from "lucide-react";
import { apiService } from "../../services/apiService";
import { INVENTORY_FIELD_SEQUENCE } from "../../config/inventoryFields";
import {
  getFieldLabel,
  isDateField,
  formatDateForInput,
  isTextareaField,
  isReadOnlyField,
  isDropdownField,
  getDropdownOptions,
  getMinDate,
  getAddressFieldLabel,
  getFieldsForProviderCategory,
} from "./editModalUtils";
import YmdDateInput from "../ui/YmdDateInput";
import CustomSelect from "../ui/CustomSelect";
import DisconnectRequestModal from "./DisconnectRequestModal";
import DisconnectRequestDetailModal from "./DisconnectRequestDetailModal";
import Toast from "../ui/Toast";
import {
  getDisconnectByCircuit,
  statusLabel,
} from "../../services/disconnectService";
import {
  formatCurrency,
  formatNumberWithCommas,
  formatPhoneNumber,
  formatConcatenatedAddress,
} from "../../utils/helpers";
import { getInventoryColumnLabel } from "../../utils/columnLabelUtils";
import { INVENTORY_FIELD_LABELS } from "../../config/inventoryFields";
import { formatDateYmdStable } from "../../utils/dateUtils";

// Helper function to format date for display (date only, no time)
// const formatDateOnly = (dateString) => {
//   if (!dateString) return "-";
//   try {
//     const date = new Date(dateString);
//     console.log("date", dateString, date);
//     if (isNaN(date.getTime())) return "-";
//     return date.toLocaleDateString("en-US", {
//       year: "numeric",
//       month: "2-digit",
//       day: "2-digit",
//     });
//   } catch (error) {
//     console.error("Error formatting date:", dateString, error);
//     return "-";
//   }
// };

const formatDateOnly = (dateString) => {
  if (!dateString) return "-";
  return formatDateYmdStable(dateString) || "-";
};

const ENABLED_FIELDS_STORAGE_PREFIX = "inventory_edit_enabled_fields";

const getEnabledFieldsStorageKey = (tenantId) =>
  tenantId
    ? `${ENABLED_FIELDS_STORAGE_PREFIX}_${tenantId}`
    : ENABLED_FIELDS_STORAGE_PREFIX;

const loadPersistedEnabledFields = (tenantId, recordId) => {
  if (recordId === undefined || recordId === null) return new Set();
  try {
    const raw = localStorage.getItem(getEnabledFieldsStorageKey(tenantId));
    const parsed = raw ? JSON.parse(raw) : {};
    const list = parsed[String(recordId)] || [];
    return new Set(list.map((field) => String(field).toLowerCase()));
  } catch (error) {
    console.error("Failed to load enabled fields:", error);
    return new Set();
  }
};

const savePersistedEnabledFields = (tenantId, recordId, fieldsSet) => {
  if (recordId === undefined || recordId === null) return;
  try {
    const key = getEnabledFieldsStorageKey(tenantId);
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : {};
    parsed[String(recordId)] = Array.from(fieldsSet);
    localStorage.setItem(key, JSON.stringify(parsed));
  } catch (error) {
    console.error("Failed to persist enabled fields:", error);
  }
};

// Exact 24 fields in order as specified by user
// Note: Address fields (std_*) are rendered separately at top in Address Section
// This order is for the regular fields section
const FIELD_ORDER = [
  "provider",
  "category",
  "circuit_id",
  "service_type",
  "status",
  "mrc",
  "last_mile_provider",
  "provider_circuit_id",
  "bandwidth",
  "contract_term",
  "end_of_term",
  "billing_account",
  "first_name",
  "last_name",
  "phone",
  "email",
  "notes",
  "follow_up_date",
  "action",
];

// Address fields (Address Information section)
const ADDRESS_FIELDS = [
  "std_street_address",
  "std_city",
  "std_state",
  "std_zip",
  "std_country",
];

const SERVICE_DETAIL_FIELDS = [
  "provider",
  "category",
  "circuit_id",
  "service_type",
  "status",
  "mrc",
];

const NOTES_FOLLOWUP_FIELDS = ["notes", "follow_up_date", "action"];

const TECHNICAL_CONTACT_FIELDS = [
  "last_mile_provider",
  "provider_circuit_id",
  "bandwidth",
  "contract_term",
  "end_of_term",
  "billing_account",
  "first_name",
  "last_name",
  "phone",
  "email",
];

/** Map std_* fields to raw address columns when std_* is empty (e.g. needs_master). */
const STD_TO_RAW_ADDRESS = {
  std_street_address: "street_address",
  std_city: "city",
  std_state: "state",
  std_zip: "zip",
  std_country: "country",
};

const resolveAddressFieldValue = (source, fieldName) => {
  if (!source) return "";
  const stdVal = source[fieldName];
  if (stdVal !== undefined && stdVal !== null && String(stdVal).trim() !== "") {
    return stdVal;
  }
  const rawKey = STD_TO_RAW_ADDRESS[fieldName];
  if (rawKey) {
    const rawVal = source[rawKey];
    if (rawVal !== undefined && rawVal !== null && String(rawVal).trim() !== "") {
      return rawVal;
    }
  }
  if (stdVal !== undefined && stdVal !== null) return stdVal;
  return "";
};

const EditModal = ({
  item,
  onClose,
  onSave,
  tenant,
  tenantId,
  originalItem,
  isNewRecord = false,
  provider = "",
  category = "",
  fieldMapping = {},
  apiColumns = [],
  fieldMappingLookup = {},
  providers = [],
  categories = [],
  statuses = [],
  serviceTypes = [],
  /** When true (e.g. Detailed Inventory), save creates new address and moves only this circuit; when false, updates address in place for all circuits at that address */
  useAddressForRecordOnly = false,
  /** false for tenant viewers (e.g. Read Only) — fields are display-only, no save (inventory + import) */
  canMutate = true,
  /** When view-only, clarifies modal title (e.g. historical blob snapshot) */
  viewContext = null,
}) => {
  // Use only dynamic values from props (no fallback)
  const sortedProviders = [...providers].sort();
  const sortedCategories = [...categories].sort();

  // Deduplicate array case-insensitively, preferring properly capitalized versions
  const deduplicateArray = (array) => {
    if (!array || array.length === 0) return [];
    const seen = new Map(); // Use Map to preserve order and track lowercase versions
    array.forEach((item) => {
      const lowerItem = String(item).toLowerCase();
      // Only add if not seen before, or if current one starts with uppercase (prefer capitalized)
      if (!seen.has(lowerItem) || (item && item[0] === item[0].toUpperCase())) {
        seen.set(lowerItem, item);
      }
    });
    return Array.from(seen.values());
  };

  const deduplicatedStatuses = deduplicateArray(statuses);
  const deduplicatedServiceTypes = deduplicateArray(serviceTypes);
  const [formData, setFormData] = useState({});
  const [showDisconnectCreateModal, setShowDisconnectCreateModal] =
    useState(false);
  const [showDisconnectViewModal, setShowDisconnectViewModal] = useState(false);
  const [linkedDisconnect, setLinkedDisconnect] = useState(null);
  const [disconnectToast, setDisconnectToast] = useState({
    title: "",
    message: null,
    type: "success",
  });
  const clearDisconnectToast = useCallback(() => {
    setDisconnectToast({
      title: "",
      message: null,
      type: "success",
    });
  }, []);
  const [standardizedAddressData, setStandardizedAddressData] = useState(null);
  const [isStandardizing, setIsStandardizing] = useState(false);
  const [isStandardizedAddressUsed, setIsStandardizedAddressUsed] =
    useState(false);
  const [mappedFields, setMappedFields] = useState(new Set());
  const [isLoadingMapping, setIsLoadingMapping] = useState(false);
  const [mrcFieldFocused, setMrcFieldFocused] = useState(false);
  const [circuitIdFieldFocused, setCircuitIdFieldFocused] = useState(false);
  const [phoneFieldFocused, setPhoneFieldFocused] = useState(false);
  const [persistedEnabledFields, setPersistedEnabledFields] = useState(
    new Set()
  );
  const previousItemIdRef = useRef(null);
  const storageTenantId = tenantId || tenant?.id || "default";

  // Detect import mode
  const isImportMode =
    apiColumns &&
    apiColumns.length > 0 &&
    (apiColumns.some(
      (col) => col === "NIT" || col === "Provider" || col === "Service Number"
    ) ||
      item?.job_id !== undefined ||
      item?.import_status !== undefined ||
      item?.temp_id !== undefined);

  /** No field edits or save when tenant user lacks mutation role (inventory + import preview). */
  const tenantViewOnly = !canMutate;

  // Load open disconnect request for this inventory circuit (if any)
  useEffect(() => {
    if (isImportMode || isNewRecord || !item?.id || !tenantId) {
      setLinkedDisconnect(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await getDisconnectByCircuit(tenantId, {
          inventoryId: item.id,
          circuitId: item.circuit_id,
        });
        if (!cancelled) setLinkedDisconnect(res?.data || null);
      } catch {
        if (!cancelled) setLinkedDisconnect(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isImportMode, isNewRecord, item?.id, item?.circuit_id, tenantId]);

  // Lock page scroll while drawer is open — only the panel body scrolls
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    document.body.style.overflow = "hidden";
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, []);

  // Detect comparison mode (3-column layout)
  const isComparisonMode =
    isImportMode && item?.import_status === "updated" && originalItem !== null;

  // Initialize form data
  useEffect(() => {
    if (!item) {
      setFormData({});
      setStandardizedAddressData(null);
      setIsStandardizing(false);
      setIsStandardizedAddressUsed(false);
      setCircuitIdFieldFocused(false);
      setPhoneFieldFocused(false);
      previousItemIdRef.current = null;
      return;
    }

    const currentItemId = item?.id;
    const isDifferentItem = previousItemIdRef.current !== currentItemId;

    if (isDifferentItem && previousItemIdRef.current !== null) {
      setFormData({});
      setStandardizedAddressData(null);
      setIsStandardizing(false);
      setIsStandardizedAddressUsed(false);
      setCircuitIdFieldFocused(false);
      setPhoneFieldFocused(false);
    }

    previousItemIdRef.current = currentItemId;

    const formattedItem = {};

    // Always include all 24 fields, initialize with empty strings
    // Include both regular fields and address fields
    [...FIELD_ORDER, ...ADDRESS_FIELDS].forEach((fieldName) => {
      formattedItem[fieldName] = "";
    });

    // For inventory mode: use item data directly
    // For import mode: use apiColumns and fieldMappingLookup
    if (isImportMode && apiColumns && apiColumns.length > 0) {
      // Import mode: map from input columns to database columns
      apiColumns.forEach((columnName) => {
        let value = item[columnName];

        // Try fieldMappingLookup reverse lookup
        if (
          (value === null || value === undefined) &&
          fieldMappingLookup &&
          Object.keys(fieldMappingLookup).length > 0
        ) {
          const mappedDbColumn = fieldMappingLookup[columnName];
          if (mappedDbColumn && item[mappedDbColumn] !== undefined) {
            value = item[mappedDbColumn];
          }
        }

        // Map input column to database column if mapping exists
        const dbColumn = fieldMappingLookup[columnName];
        if (dbColumn) {
          formattedItem[dbColumn] =
            value === null || value === undefined ? "" : value;
        } else {
          // Direct match (case-insensitive)
          const fieldLower = columnName.toLowerCase();
          const matchingField = FIELD_ORDER.find(
            (f) => f.toLowerCase() === fieldLower
          );
          if (matchingField) {
            formattedItem[matchingField] =
              value === null || value === undefined ? "" : value;
          }
        }
      });
    } else {
      // Inventory mode: use item data directly
      [...FIELD_ORDER, ...ADDRESS_FIELDS].forEach((fieldName) => {
        if (item[fieldName] !== undefined) {
          formattedItem[fieldName] =
            item[fieldName] === null || item[fieldName] === undefined
              ? ""
              : item[fieldName];
        }
      });
    }

    // Always ensure provider, category, notes, follow_up_date are set
    // Provider
    let providerValue = item.provider;
    if (providerValue === undefined || providerValue === null) {
      const variants = ["provider", "Provider"];
      for (const variant of variants) {
        if (item[variant] !== undefined) {
          providerValue = item[variant];
          break;
        }
      }
      if ((providerValue === undefined || providerValue === null) && provider) {
        providerValue = provider;
      }
    }
    if (providerValue) {
      const matchingProvider = sortedProviders.find(
        (p) =>
          p &&
          typeof p === "string" &&
          p.toLowerCase() === String(providerValue).toLowerCase()
      );
      if (matchingProvider) {
        providerValue = matchingProvider;
      }
    }
    formattedItem.provider = providerValue || "";

    // Category
    let categoryValue = item.category;
    if (categoryValue === undefined || categoryValue === null) {
      const variants = ["category", "Category"];
      for (const variant of variants) {
        if (item[variant] !== undefined) {
          categoryValue = item[variant];
          break;
        }
      }
      if ((categoryValue === undefined || categoryValue === null) && category) {
        categoryValue = category;
      }
    }
    if (categoryValue) {
      const matchingCategory = sortedCategories.find(
        (c) =>
          c &&
          typeof c === "string" &&
          String(c).toLowerCase() === String(categoryValue).toLowerCase()
      );
      if (matchingCategory) {
        categoryValue = matchingCategory;
      }
    }
    formattedItem.category = categoryValue || "";

    // Notes
    formattedItem.notes =
      item.notes !== undefined && item.notes !== null ? item.notes : "";

    // Follow up date - check multiple variations
    let followUpDateValue = null;
    if (
      item.follow_up_date !== undefined &&
      item.follow_up_date !== null &&
      item.follow_up_date !== ""
    ) {
      followUpDateValue = item.follow_up_date;
    } else if (
      item["Follow Up Date"] !== undefined &&
      item["Follow Up Date"] !== null &&
      item["Follow Up Date"] !== ""
    ) {
      followUpDateValue = item["Follow Up Date"];
    } else if (
      item["Follow-Up Date"] !== undefined &&
      item["Follow-Up Date"] !== null &&
      item["Follow-Up Date"] !== ""
    ) {
      followUpDateValue = item["Follow-Up Date"];
    }
    formattedItem.follow_up_date =
      followUpDateValue !== null && followUpDateValue !== ""
        ? followUpDateValue
        : "";

    // Action - check multiple variations
    let actionValue = null;
    if (item.action !== undefined && item.action !== null && item.action !== "") {
      actionValue = item.action;
    } else if (item["Action"] !== undefined && item["Action"] !== null && item["Action"] !== "") {
      actionValue = item["Action"];
    }
    formattedItem.action = actionValue !== null && actionValue !== "" ? actionValue : "";

    // Normalize MRC to string
    if (item.mrc !== undefined && item.mrc !== null) {
      formattedItem.mrc = String(item.mrc).trim();
    }

    // Normalize bandwidth
    if (item.bandwidth !== undefined) {
      formattedItem.bandwidth = item.bandwidth || "";
    }

    // Format date fields
    Object.keys(formattedItem).forEach((key) => {
      if (isDateField(key)) {
        formattedItem[key] = formatDateForInput(formattedItem[key]);
      }
    });

    // Preserve std_* address fields (fall back to raw address when std_* empty)
    formattedItem.std_street_address = resolveAddressFieldValue(
      item,
      "std_street_address"
    );
    formattedItem.std_city = resolveAddressFieldValue(item, "std_city");
    formattedItem.std_state = resolveAddressFieldValue(item, "std_state");
    formattedItem.std_zip = resolveAddressFieldValue(item, "std_zip");
    formattedItem.std_country = resolveAddressFieldValue(
      item,
      "std_country"
    );

    // Initialize verified field for import mode
    if (isImportMode) {
      let verifiedValue = false;
      if (item.verified !== undefined) {
        if (typeof item.verified === "boolean") {
          verifiedValue = item.verified;
        } else if (typeof item.verified === "string") {
          verifiedValue =
            item.verified.toLowerCase() === "true" || item.verified === "1";
        } else if (typeof item.verified === "number") {
          verifiedValue = item.verified === 1;
        }
      }
      formattedItem.verified = verifiedValue;
    }

    setFormData(formattedItem);

    // Reset standardized address state
    setStandardizedAddressData(null);
    setIsStandardizedAddressUsed(false);

    // Handle address standardization
    const extractStdAddressComponents = (item) => {
      if (!item) return null;

      const std_street_address = item.std_street_address || "";
      const std_city = item.std_city || "";
      const std_state = item.std_state || "";
      const std_zip = item.std_zip || "";
      const std_country = item.std_country || "US";

      if (!std_street_address && !std_city && !std_state && !std_zip) {
        return null;
      }

      return {
        street_address: std_street_address,
        city: std_city,
        state: std_state,
        zip: std_zip,
        country: std_country || "US",
      };
    };

    const addressComponents = extractStdAddressComponents(item);

    if (addressComponents && canMutate) {
      setIsStandardizing(true);
      apiService
        .standardizeAddress(addressComponents)
        .then((response) => {
          const standardizedData = response.data || response;
          if (
            standardizedData &&
            (standardizedData.standard_address ||
              standardizedData.std_street_address ||
              standardizedData.std_city ||
              standardizedData.std_state ||
              standardizedData.std_zip)
          ) {
            setStandardizedAddressData(standardizedData);
          } else {
            setStandardizedAddressData(null);
          }
          setIsStandardizing(false);
        })
        .catch((error) => {
          console.error("Address standardization failed:", error);
          setIsStandardizing(false);
          setStandardizedAddressData(null);
        });
    } else {
      setStandardizedAddressData(null);
    }
  }, [
    item?.id,
    item?.follow_up_date,
    item?.notes,
    isImportMode,
    apiColumns,
    fieldMappingLookup,
    provider,
    category,
    canMutate,
  ]);

  // Fetch mapping data
  useEffect(() => {
    const currentProvider = provider || item?.provider || "";
    const currentCategory = category || item?.category || "";
    const effectiveTenantId = tenantId;

    if (!currentProvider || !currentCategory || !effectiveTenantId) {
      setMappedFields(new Set());
      return;
    }

    // For import mode, use fieldMappingLookup
    if (
      isImportMode &&
      fieldMappingLookup &&
      Object.keys(fieldMappingLookup).length > 0
    ) {
      const mappedFieldsSet = new Set();
      Object.values(fieldMappingLookup).forEach((inventoryColumn) => {
        if (inventoryColumn && typeof inventoryColumn === "string") {
          mappedFieldsSet.add(inventoryColumn.toLowerCase());
        }
      });
      setMappedFields(mappedFieldsSet);
      return;
    }

    // For inventory mode, fetch from API
    const fetchMapping = async () => {
      try {
        setIsLoadingMapping(true);
        const response = await apiService.getColumnMapping(
          currentProvider,
          currentCategory,
          tenantId,
          "flat"
        );

        const mappedFieldsSet = new Set();

        if (response && response.success && response.data) {
          if (
            response.data.mapping_list &&
            Array.isArray(response.data.mapping_list)
          ) {
            response.data.mapping_list.forEach((mapping) => {
              if (
                mapping.inventory_column_name &&
                typeof mapping.inventory_column_name === "string"
              ) {
                mappedFieldsSet.add(
                  mapping.inventory_column_name.toLowerCase()
                );
              }
            });
          } else if (
            response.data.mappings &&
            Array.isArray(response.data.mappings)
          ) {
            response.data.mappings.forEach((mapping) => {
              if (
                mapping.inventory_column_name &&
                typeof mapping.inventory_column_name === "string"
              ) {
                mappedFieldsSet.add(
                  mapping.inventory_column_name.toLowerCase()
                );
              }
            });
          }
        } else if (Array.isArray(response)) {
          response.forEach((mapping) => {
            if (
              mapping.inventory_column_name &&
              typeof mapping.inventory_column_name === "string"
            ) {
              mappedFieldsSet.add(mapping.inventory_column_name.toLowerCase());
            }
          });
        } else if (response && response.data && Array.isArray(response.data)) {
          response.data.forEach((mapping) => {
            if (
              mapping.inventory_column_name &&
              typeof mapping.inventory_column_name === "string"
            ) {
              mappedFieldsSet.add(mapping.inventory_column_name.toLowerCase());
            }
          });
        }

        setMappedFields(mappedFieldsSet);
      } catch (error) {
        console.error("Failed to fetch mapping:", error);
        setMappedFields(new Set());
      } finally {
        setIsLoadingMapping(false);
      }
    };

    fetchMapping();
  }, [
    provider,
    category,
    item?.provider,
    item?.category,
    tenantId,
    isImportMode,
    fieldMappingLookup,
  ]);

  useEffect(() => {
    if (isImportMode) return;
    setPersistedEnabledFields(
      loadPersistedEnabledFields(storageTenantId, item?.id)
    );
  }, [storageTenantId, item?.id, isImportMode]);

  // Check if field is enabled (applicable for provider/category)
  // Address fields are always enabled, all other fields depend on mapping
  // Same logic for both inventory and import modes: mapped OR has data OR has been edited
  const isFieldEnabled = (fieldName) => {
    if (tenantViewOnly) {
      return false;
    }

    const fieldLower = fieldName.toLowerCase();

    // Address fields always enabled (handled separately in Address Section)
    if (
      fieldLower === "std_street_address" ||
      fieldLower === "std_city" ||
      fieldLower === "std_state" ||
      fieldLower === "std_zip" ||
      fieldLower === "std_country"
    ) {
      return true;
    }

    // Essential fields always enabled
    if (
      fieldLower === "provider" ||
      fieldLower === "category" ||
      fieldLower === "notes" ||
      fieldLower === "follow_up_date" ||
      fieldLower === "action"
    ) {
      return true;
    }

    // Check if field is in mappedFields (from API mapping for inventory, or fieldMappingLookup for import)
    const isMapped = mappedFields.has(fieldLower);

    // If mapped, always enable
    if (isMapped) {
      return true;
    }

    // Check if field has actual data (not just empty string) in formData OR exists in originalItem
    // This ensures fields with existing data are editable, even if not in mapping
    const hasDataInFormData =
      formData[fieldName] !== undefined &&
      formData[fieldName] !== null &&
      formData[fieldName] !== "";

    const existsInOriginalItem =
      originalItem &&
      originalItem[fieldName] !== undefined &&
      originalItem[fieldName] !== null &&
      originalItem[fieldName] !== "";

    const existsInFormData = hasDataInFormData || existsInOriginalItem;

    // Check if field has been edited (value differs from originalItem)
    // This ensures edited fields remain editable even after clearing
    let hasBeenEdited = false;
    if (originalItem && formData[fieldName] !== undefined) {
      const originalValue = getOriginalFieldValueForComparison(fieldName);
      const currentValue = formData[fieldName];

      // Normalize values for comparison
      let normalizedOriginal = "";
      let normalizedCurrent = "";

      if (fieldName === "mrc") {
        // Handle MRC as number comparison
        const parseAsNumber = (val) => {
          if (val === null || val === undefined || val === "") return null;
          const num =
            typeof val === "number" ? val : parseFloat(String(val).trim());
          return isNaN(num) ? null : num;
        };
        const origNum = parseAsNumber(originalValue);
        const currNum = parseAsNumber(currentValue);
        normalizedOriginal = origNum === null ? "" : String(origNum);
        normalizedCurrent = currNum === null ? "" : String(currNum);
      } else if (isDateField(fieldName)) {
        // Handle date fields
        normalizedOriginal = formatDateForInput(originalValue || "");
        normalizedCurrent = formatDateForInput(currentValue || "");
      } else {
        // Handle string fields
        normalizedOriginal =
          originalValue === null || originalValue === undefined
            ? ""
            : String(originalValue).trim();
        normalizedCurrent =
          currentValue === null || currentValue === undefined
            ? ""
            : String(currentValue).trim();
      }

      // Field has been edited if values differ
      hasBeenEdited = normalizedOriginal !== normalizedCurrent;
    }

    // Enable if: mapped OR has data in formData/originalItem OR has been edited
    // Mapped fields are always editable
    // Fields with existing data are editable (even if not mapped)
    // Edited fields remain editable even after clearing
    // Same logic for both inventory and import modes
    const isPersistedEnabled =
      !isImportMode && persistedEnabledFields.has(fieldLower);
    return isMapped || existsInFormData || hasBeenEdited || isPersistedEnabled;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let processedValue = value;
    if (name === "mrc" && value) {
      processedValue = value.replace(/[$,]/g, "").trim();
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));

    if (
      !isImportMode &&
      item?.id !== undefined &&
      item?.id !== null &&
      name
    ) {
      const fieldKey = String(name).toLowerCase();
      setPersistedEnabledFields((prev) => {
        if (prev.has(fieldKey)) return prev;
        const next = new Set(prev);
        next.add(fieldKey);
        savePersistedEnabledFields(storageTenantId, item.id, next);
        return next;
      });
    }

    // Track if std_* address fields are manually edited
    const stdAddressFields = [
      "std_street_address",
      "std_city",
      "std_state",
      "std_zip",
      "std_country",
    ];
    if (
      name &&
      typeof name === "string" &&
      stdAddressFields.includes(name.toLowerCase())
    ) {
      setIsStandardizedAddressUsed(false);
    }

    // Auto-verify for import mode
    if (isImportMode && name.toLowerCase() !== "verified") {
      setFormData((prev) => ({
        ...prev,
        verified: true,
      }));
    }
  };

  const handleMrcBlur = (e) => {
    const { name, value } = e.target;
    if (name === "mrc" && value) {
      const rawValue = value.replace(/[$,]/g, "");
      const numValue = parseFloat(rawValue);
      if (!isNaN(numValue)) {
        setFormData((prev) => ({
          ...prev,
          [name]: rawValue,
        }));
      }
    }
    setMrcFieldFocused(false);
  };

  const handleMrcFocus = (e) => {
    const { name, value } = e.target;
    if (name === "mrc" && value) {
      const rawValue = value.replace(/[$,]/g, "");
      setFormData((prev) => ({
        ...prev,
        [name]: rawValue,
      }));
    }
    setMrcFieldFocused(true);
  };

  const handlePhoneFieldBlur = (fieldName) => {
    if (fieldName === "circuit_id") {
      setCircuitIdFieldFocused(false);
    } else if (fieldName === "phone") {
      setPhoneFieldFocused(false);
    }
  };

  const handlePhoneFieldFocus = (fieldName) => {
    if (fieldName === "circuit_id") {
      setCircuitIdFieldFocused(true);
    } else if (fieldName === "phone") {
      setPhoneFieldFocused(true);
    }
  };

  const handleUseStandard = () => {
    if (!standardizedAddressData) return;

    const updatedFormData = { ...formData };

    updatedFormData.std_street_address =
      standardizedAddressData.std_street_address || "";
    updatedFormData.std_city = standardizedAddressData.std_city || "";
    updatedFormData.std_state = standardizedAddressData.std_state || "";
    updatedFormData.std_zip = standardizedAddressData.std_zip || "";
    updatedFormData.std_country = standardizedAddressData.std_country || "";

    setFormData(updatedFormData);
    setIsStandardizedAddressUsed(true);

    if (isImportMode) {
      setFormData((prev) => ({
        ...prev,
        verified: true,
      }));
    }
  };

  // Get original field value for comparison
  const getOriginalFieldValue = (fieldName) => {
    if (!originalItem) return null;

    if (fieldName === "mrc") {
      const value = originalItem.mrc;
      if (value === null || value === undefined) return null;
      return String(value).trim();
    }

    if (isDateField(fieldName)) {
      const value = originalItem[fieldName];
      if (value === null || value === undefined) return null;
      return formatDateForInput(value);
    }

    return originalItem[fieldName];
  };

  const getChanges = () => {
    if (!originalItem) return null;

    const changes = {};
    Object.keys(formData).forEach((fieldName) => {
      if (isImportMode && fieldName.toLowerCase() === "provider") {
        return;
      }

      const originalValue = getOriginalFieldValueForComparison(fieldName);
      const newValue = formData[fieldName];

      let normalizedOriginal = "";
      let normalizedNew = "";

      if (fieldName === "mrc") {
        const parseAsNumber = (val) => {
          if (val === null || val === undefined || val === "") return null;
          const num =
            typeof val === "number" ? val : parseFloat(String(val).trim());
          return isNaN(num) ? null : num;
        };

        const origNum = parseAsNumber(originalValue);
        const newNum = parseAsNumber(newValue);

        if (origNum === null && newNum === null) {
          normalizedOriginal = "";
          normalizedNew = "";
        } else {
          const roundToTwoDecimals = (num) => {
            if (num === null || num === undefined) return null;
            return Math.round((num + Number.EPSILON) * 100) / 100;
          };
          const roundedOrig = roundToTwoDecimals(origNum);
          const roundedNew = roundToTwoDecimals(newNum);
          normalizedOriginal = roundedOrig === null ? "" : String(roundedOrig);
          normalizedNew = roundedNew === null ? "" : String(roundedNew);
        }
      } else if (isDateField(fieldName)) {
        normalizedOriginal = formatDateForInput(originalValue || "");
        normalizedNew = formatDateForInput(newValue || "");
      } else {
        normalizedOriginal =
          originalValue === null || originalValue === undefined
            ? ""
            : String(originalValue).trim();
        normalizedNew =
          newValue === null || newValue === undefined
            ? ""
            : String(newValue).trim();
      }

      if (normalizedOriginal !== normalizedNew) {
        changes[fieldName] = {
          original: normalizedOriginal,
          new: normalizedNew,
          changed: true,
        };
      }
    });

    return Object.keys(changes).length > 0 ? changes : null;
  };

  // Check if there are any changes to enable/disable save button
  const hasChanges = useMemo(() => {
    // For new records, always allow saving
    if (isNewRecord) return true;

    if (!originalItem) return false;

    // Check if there are field changes
    const changes = {};
    Object.keys(formData).forEach((fieldName) => {
      if (isImportMode && fieldName.toLowerCase() === "provider") {
        return;
      }

      const originalValue = getOriginalFieldValue(fieldName);
      const newValue = formData[fieldName];

      let normalizedOriginal = "";
      let normalizedNew = "";

      if (fieldName === "mrc") {
        const parseAsNumber = (val) => {
          if (val === null || val === undefined || val === "") return null;
          const num =
            typeof val === "number" ? val : parseFloat(String(val).trim());
          return isNaN(num) ? null : num;
        };

        const origNum = parseAsNumber(originalValue);
        const newNum = parseAsNumber(newValue);

        if (origNum === null && newNum === null) {
          normalizedOriginal = "";
          normalizedNew = "";
        } else {
          const roundToTwoDecimals = (num) => {
            if (num === null || num === undefined) return null;
            return Math.round((num + Number.EPSILON) * 100) / 100;
          };
          const roundedOrig = roundToTwoDecimals(origNum);
          const roundedNew = roundToTwoDecimals(newNum);
          normalizedOriginal = roundedOrig === null ? "" : String(roundedOrig);
          normalizedNew = roundedNew === null ? "" : String(roundedNew);
        }
      } else if (isDateField(fieldName)) {
        normalizedOriginal = formatDateForInput(originalValue || "");
        normalizedNew = formatDateForInput(newValue || "");
      } else {
        normalizedOriginal =
          originalValue === null || originalValue === undefined
            ? ""
            : String(originalValue).trim();
        normalizedNew =
          newValue === null || newValue === undefined
            ? ""
            : String(newValue).trim();
      }

      if (normalizedOriginal !== normalizedNew) {
        changes[fieldName] = true;
      }
    });

    if (Object.keys(changes).length > 0) return true;

    // Check if address fields have changed
    if (
      originalItem &&
      item.address_id !== undefined &&
      item.address_id !== null
    ) {
      const addressFields = [
        "std_street_address",
        "std_city",
        "std_state",
        "std_zip",
        "std_country",
      ];
      const hasAddressChanges = addressFields.some((fieldName) => {
        const originalValue = String(
          resolveAddressFieldValue(originalItem, fieldName) || ""
        ).trim();
        const newValue =
          formData[fieldName] === null || formData[fieldName] === undefined
            ? ""
            : String(formData[fieldName] || "").trim();
        return originalValue !== newValue;
      });
      if (hasAddressChanges) return true;
    }

    // Check if standardized address was used (that's also a change)
    if (isStandardizedAddressUsed && standardizedAddressData) return true;

    // Check if verified status changed (for import mode)
    if (isImportMode) {
      const originalVerified = originalItem.verified || false;
      const currentVerified = formData.verified || false;
      if (originalVerified !== currentVerified) return true;
    }

    return false;
  }, [
    formData,
    originalItem,
    item,
    isNewRecord,
    isStandardizedAddressUsed,
    standardizedAddressData,
    isImportMode,
  ]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (tenantViewOnly) {
      return;
    }

    // Don't proceed if there are no changes (unless it's a new record)
    if (!hasChanges) {
      return;
    }

    const changes = getChanges();

    // For inventory mode, use form data directly
    // For import mode, map using fieldMappingLookup
    let updatedItem = {
      ...item,
      ...formData,
    };

    if (
      isImportMode &&
      fieldMappingLookup &&
      Object.keys(fieldMappingLookup).length > 0
    ) {
      const mappedData = {};
      Object.keys(changes || {}).forEach((fieldName) => {
        if (formData[fieldName] !== undefined) {
          mappedData[fieldName] = formData[fieldName];
        }
      });

      Object.keys(mappedData).forEach((fieldKey) => {
        const inventoryColumnName = fieldMappingLookup[fieldKey];
        if (inventoryColumnName && mappedData[fieldKey] !== undefined) {
          mappedData[inventoryColumnName] = mappedData[fieldKey];
        }
      });

      updatedItem = {
        ...item,
        ...mappedData,
      };
    }

    if (!isImportMode && item?.id !== undefined && item?.id !== null) {
      const mergedEnabledFields = new Set(persistedEnabledFields);
      Object.keys(changes || {}).forEach((fieldName) => {
        mergedEnabledFields.add(String(fieldName).toLowerCase());
      });
      if (mergedEnabledFields.size !== persistedEnabledFields.size) {
        setPersistedEnabledFields(mergedEnabledFields);
      }
      savePersistedEnabledFields(storageTenantId, item.id, mergedEnabledFields);
    }

    // Handle address save
    const saveWithAddressUpdate = async () => {
      const effectiveTenantId =
        tenantId ?? tenant?.id ?? tenant?.tenant_id ?? null;
      let itemForSave = updatedItem;
      if (item.address_id !== undefined && item.address_id !== null) {
        try {
          if (isStandardizedAddressUsed && standardizedAddressData) {
            if (useAddressForRecordOnly && item.id != null && effectiveTenantId != null) {
              const addressRes = await apiService.standardizeAndSaveAddressForRecord({
                address_id: item.address_id,
                record_id: item.id,
                tenant_id: effectiveTenantId,
                std_street_address: standardizedAddressData.std_street_address,
                std_city: standardizedAddressData.std_city,
                std_state: standardizedAddressData.std_state,
                std_zip: standardizedAddressData.std_zip,
                std_country: standardizedAddressData.std_country,
                version: item.version,
              });
              if (addressRes?.inventory_version != null) {
                itemForSave = {
                  ...itemForSave,
                  version: addressRes.inventory_version,
                  address_id:
                    addressRes.address?.address_id ?? itemForSave.address_id,
                  address_version:
                    addressRes.address?.version ?? itemForSave.address_version,
                };
              }
            } else {
              await apiService.standardizeAndSaveAddress({
                address_id: item.address_id,
                std_street_address: standardizedAddressData.std_street_address,
                std_city: standardizedAddressData.std_city,
                std_state: standardizedAddressData.std_state,
                std_zip: standardizedAddressData.std_zip,
                std_country: standardizedAddressData.std_country,
                standard_address: standardizedAddressData.standard_address,
                version: item.address_version,
              });
            }
          } else if (
            !isStandardizedAddressUsed &&
            (formData.std_street_address !== undefined ||
              formData.std_city !== undefined ||
              formData.std_state !== undefined ||
              formData.std_zip !== undefined ||
              formData.std_country !== undefined)
          ) {
            const hasStdFieldChanges =
              originalItem &&
              (String(resolveAddressFieldValue(originalItem, "std_street_address") || "") !==
                String(formData.std_street_address || "") ||
                String(resolveAddressFieldValue(originalItem, "std_city") || "") !==
                  String(formData.std_city || "") ||
                String(resolveAddressFieldValue(originalItem, "std_state") || "") !==
                  String(formData.std_state || "") ||
                String(resolveAddressFieldValue(originalItem, "std_zip") || "") !==
                  String(formData.std_zip || "") ||
                String(resolveAddressFieldValue(originalItem, "std_country") || "") !==
                  String(formData.std_country || ""));

            if (hasStdFieldChanges) {
              if (useAddressForRecordOnly && item.id != null && effectiveTenantId != null) {
                const addressRes = await apiService.standardizeAndSaveAddressForRecord({
                  address_id: item.address_id,
                  record_id: item.id,
                  tenant_id: effectiveTenantId,
                  std_street_address: formData.std_street_address || "",
                  std_city: formData.std_city || "",
                  std_state: formData.std_state || "",
                  std_zip: formData.std_zip || "",
                  std_country: formData.std_country || "",
                  version: item.version,
                });
                if (addressRes?.inventory_version != null) {
                  itemForSave = {
                    ...itemForSave,
                    version: addressRes.inventory_version,
                    address_id:
                      addressRes.address?.address_id ?? itemForSave.address_id,
                    address_version:
                      addressRes.address?.version ?? itemForSave.address_version,
                  };
                }
              } else {
                const standardAddressParts = [];
                if (formData.std_street_address) {
                  standardAddressParts.push(formData.std_street_address);
                }
                if (formData.std_city) {
                  standardAddressParts.push(formData.std_city);
                }
                if (formData.std_state) {
                  standardAddressParts.push(formData.std_state);
                }
                if (formData.std_country) {
                  standardAddressParts.push(formData.std_country);
                }
                if (formData.std_zip) {
                  standardAddressParts.push(formData.std_zip);
                }
                const standardAddress = standardAddressParts.join(", ");
                await apiService.standardizeAndSaveAddress({
                  address_id: item.address_id,
                  std_street_address: formData.std_street_address || "",
                  std_city: formData.std_city || "",
                  std_state: formData.std_state || "",
                  std_zip: formData.std_zip || "",
                  std_country: formData.std_country || "",
                  standard_address: standardAddress,
                  version: item.address_version,
                });
              }
            }
          }
        } catch (error) {
          console.error("Failed to save address:", error);
          // Stop here so InventoryPage does not re-save with a stale version
          // and show a false "updated by someone else" toast.
          window.alert(
            error?.message ||
              "Failed to save address. Please reload and try again.",
          );
          return;
        }
      }

      // Filter out address fields from changes
      const addressFields = [
        "std_street_address",
        "std_city",
        "std_state",
        "std_zip",
        "std_country",
        "standard_address",
        "address_id",
      ];

      let filteredChanges = null;
      if (changes && Object.keys(changes).length > 0) {
        filteredChanges = {};
        Object.keys(changes).forEach((field) => {
          if (!addressFields.includes(field)) {
            filteredChanges[field] = changes[field];
          }
        });

        if (Object.keys(filteredChanges).length === 0) {
          filteredChanges = null;
        }
      }

      onSave(itemForSave, filteredChanges);
    };

    saveWithAddressUpdate();
  };

  if (!item) return null;

  const modalEyebrow = tenantViewOnly
    ? isImportMode
      ? "View import item"
      : viewContext === "snapshot"
        ? "View snapshot record"
        : "View inventory item"
    : isNewRecord && !originalItem
      ? isImportMode
        ? "Add new import item"
        : "Add new inventory item"
      : isImportMode
        ? "Edit import item"
        : "Edit inventory item";
  const modalCircuitTitle =
    formData.circuit_id || item.circuit_id || modalEyebrow;
  const modalStatus = formData.status || item.status || "";
  const modalStatusLower = String(modalStatus).toLowerCase().trim();
  const modalStatusClass =
    modalStatusLower === "in service" || modalStatusLower === "active"
      ? "text-[var(--good,#1f6b34)]"
      : modalStatusLower.includes("cancel") ||
        (modalStatusLower.includes("disconnect") &&
          !modalStatusLower.includes("pending"))
        ? "text-[var(--crit,#c0262b)]"
        : "text-[var(--muted,#6b6a66)]";
  const modalAddress = formatConcatenatedAddress({ ...item, ...formData });
  const updatedBy = item.updated_by || item.last_updated_by || "";
  const updatedAtLabel = item.updated_at ? formatDateOnly(item.updated_at) : "";

  // Get original field value for comparison (handles import mode field mapping)
  const getOriginalFieldValueForComparison = (fieldName) => {
    if (!originalItem) return null;

    // For import mode, check fieldMappingLookup
    if (
      isImportMode &&
      fieldMappingLookup &&
      Object.keys(fieldMappingLookup).length > 0
    ) {
      // Check if fieldName is a database column, find input column
      let inputColumn = null;
      Object.entries(fieldMappingLookup).forEach(([inputCol, dbCol]) => {
        if (dbCol === fieldName) {
          inputColumn = inputCol;
        }
      });

      // Try input column first, then database column
      if (inputColumn && originalItem[inputColumn] !== undefined) {
        return originalItem[inputColumn];
      }
      if (originalItem[fieldName] !== undefined) {
        return originalItem[fieldName];
      }
    }

    // For inventory mode or fallback, use direct lookup
    return getOriginalFieldValue(fieldName);
  };

  // Get original field value for address fields in comparison mode
  const getOriginalAddressValue = (fieldName) => {
    if (!originalItem) return null;
    return originalItem[fieldName] || null;
  };

  // Render a single field
  const renderField = (fieldName) => {
    // Special handling for MRC to always show "Mrc$"
    // Special handling for contract_term to always show "Contract Term (months)"
    let fieldLabel;
    if (fieldName.toLowerCase() === "mrc") {
      fieldLabel = "Mrc";
    } else if (fieldName.toLowerCase() === "contract_term") {
      fieldLabel = "Contract Term (months)";
    } else {
      fieldLabel = isImportMode
        ? getInventoryColumnLabel(
            fieldName,
            fieldMappingLookup,
            INVENTORY_FIELD_LABELS
          )
        : getFieldLabel(fieldName);
    }
    const value = formData[fieldName] !== undefined ? formData[fieldName] : "";
    const isDate = isDateField(fieldName);
    const isNotesField = fieldName.toLowerCase() === "notes";
    const isTextarea =
      isNotesField || isTextareaField(fieldName, fieldMappingLookup);
    const isDropdown = isDropdownField(fieldName);
    const isReadOnly =
      isReadOnlyField(fieldName, isImportMode) ||
      (!isImportMode && fieldName.toLowerCase() === "billing_account");
    const dropdownOptions = isDropdown
      ? getDropdownOptions(fieldName, {
          providers: sortedProviders,
          categories: sortedCategories,
          statuses: deduplicatedStatuses,
          serviceTypes: deduplicatedServiceTypes,
        })
      : [];
    const enabled = isFieldEnabled(fieldName);

    // Get original value for comparison
    let originalValue = getOriginalFieldValueForComparison(fieldName);

    // Check if field is in modified_fields (from backend comparison)
    let modifiedFieldData = null;
    if (item && item.modified_fields) {
      const modifiedFields =
        typeof item.modified_fields === "string"
          ? JSON.parse(item.modified_fields)
          : item.modified_fields;

      // Check if this field is in modified_fields
      modifiedFieldData = modifiedFields[fieldName];

      // If found, use the values from modified_fields
      if (modifiedFieldData && modifiedFieldData.changed) {
        originalValue = modifiedFieldData.old_value;
      }
    }

    // Normalize both original and new values for proper comparison
    // This matches the logic from EditImportModal.js
    let normalizedOriginal = "";
    let normalizedNew = "";

    if (isDate) {
      // Format both values using formatDateForInput for consistent comparison
      normalizedOriginal = formatDateForInput(originalValue || "");
      normalizedNew = formatDateForInput(value || "");
    } else {
      // Special handling for numeric fields like MRC, bandwidth, contract_term
      const isNumericField =
        fieldName.toLowerCase() === "mrc" ||
        fieldName.toLowerCase() === "bandwidth" ||
        fieldName.toLowerCase() === "contract_term" ||
        fieldName.toLowerCase() === "total";

      if (isNumericField) {
        // Convert both values to numbers, then to strings for comparison
        const parseAsNumber = (val) => {
          if (val === null || val === undefined || val === "") {
            return null;
          }
          const num =
            typeof val === "number" ? val : parseFloat(String(val).trim());
          return isNaN(num) ? null : num;
        };

        const origNum = parseAsNumber(originalValue);
        const newNum = parseAsNumber(value);

        // If both are null/empty, they're equal
        if (origNum === null && newNum === null) {
          normalizedOriginal = "";
          normalizedNew = "";
        } else {
          // For MRC and Total specifically, use fixed decimal precision
          if (
            fieldName.toLowerCase() === "mrc" ||
            fieldName.toLowerCase() === "total"
          ) {
            const roundToTwoDecimals = (num) => {
              if (num === null || num === undefined) return null;
              return Math.round((num + Number.EPSILON) * 100) / 100;
            };
            const roundedOrig = roundToTwoDecimals(origNum);
            const roundedNew = roundToTwoDecimals(newNum);
            normalizedOriginal =
              roundedOrig === null ? "" : String(roundedOrig);
            normalizedNew = roundedNew === null ? "" : String(roundedNew);
          } else {
            // For other numeric fields, convert to string
            normalizedOriginal = origNum === null ? "" : String(origNum);
            normalizedNew = newNum === null ? "" : String(newNum);
          }
        }
      } else {
        // For non-numeric, non-date fields, normalize as strings
        normalizedOriginal =
          originalValue === null || originalValue === undefined
            ? ""
            : String(originalValue).trim();
        normalizedNew =
          value === null || value === undefined ? "" : String(value).trim();
      }
    }

    // Only show as edited if:
    // 1. originalItem exists OR modified_fields has this field
    // 2. The normalized values are actually different
    const isEdited =
      (originalItem !== null || modifiedFieldData !== null) &&
      normalizedOriginal !== normalizedNew;

    const baseInputClasses = isComparisonMode
      ? `w-full border rounded py-2 px-3 text-xs focus:ring-1 focus:ring-${tenant.primaryColor}`
      : "w-full rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-3 py-2 text-[13px] text-[var(--ink,#1c1c1a)] placeholder:text-[var(--faint,#9a9a95)] focus:outline-none focus:ring-2 focus:ring-[var(--accent,#a51c1c)]/25 focus:border-[var(--accent,#a51c1c)] disabled:bg-[var(--sunken,#eeece7)] disabled:cursor-not-allowed";
    const inputClasses = enabled
      ? `${baseInputClasses} ${
          isEdited ? "bg-yellow-50 border-yellow-400" : isComparisonMode ? "border-gray-300" : ""
        }`
      : `${baseInputClasses} bg-[var(--sunken,#eeece7)] text-[var(--muted,#6b6a66)] cursor-not-allowed`;

    const renderInput = () => {
      if (isReadOnly || !enabled) {
        // Format display value for circuit_id if category is Mobility/Analog/Voice
        let displayValue = value || "-";
        if (fieldName === "mrc" && value) {
          displayValue = formatCurrency(value);
        } else if (fieldName === "circuit_id" && value) {
          const category = formData.category || "";
          if (
            category === "Mobility" ||
            category === "Analog" ||
            category === "Voice"
          ) {
            displayValue = formatPhoneNumber(value);
          }
        } else if (fieldName === "phone" && value) {
          displayValue = formatPhoneNumber(value);
        }

        if (isNotesField) {
          return (
            <div
              className={`w-full border border-gray-300 rounded py-2 px-3 text-xs min-h-[6.5rem] whitespace-pre-wrap ${
                !enabled
                  ? "bg-gray-100 text-gray-400"
                  : "bg-gray-50 text-gray-700"
              }`}
            >
              {displayValue}
            </div>
          );
        }

        const isFigureField =
          fieldName === "mrc" ||
          fieldName === "circuit_id" ||
          fieldName === "billing_account" ||
          fieldName === "phone" ||
          fieldName === "end_of_term" ||
          fieldName === "follow_up_date" ||
          fieldName === "works_with_circuit_id";
        return (
          <div
            className={`w-full border border-gray-300 rounded py-2 px-3 text-xs ${
              !enabled
                ? "bg-gray-100 text-gray-400"
                : "bg-gray-50 text-gray-700"
            }${isFigureField ? " tabular-nums" : ""}`}
          >
            {displayValue}
          </div>
        );
      }
      if (isTextarea) {
        return (
          <textarea
            name={fieldName}
            value={value}
            onChange={handleChange}
            className={inputClasses}
            rows={isNotesField ? 4 : 2}
            placeholder={`Enter ${fieldLabel.toLowerCase()}...`}
          />
        );
      }
      if (isDropdown) {
        return (
          <CustomSelect
            name={fieldName}
            value={value}
            onChange={handleChange}
            className={inputClasses}
            disabled={!enabled}
            placeholder={`Select ${fieldLabel}`}
          >
            <option value="">Select {fieldLabel}</option>
            {(() => {
              // Use Map for case-insensitive deduplication
              const optionsMap = new Map();
              dropdownOptions.forEach((opt) => {
                const lowerOpt = String(opt).toLowerCase();
                // Prefer capitalized version if not already in map
                if (
                  !optionsMap.has(lowerOpt) ||
                  (opt && opt[0] === opt[0].toUpperCase())
                ) {
                  optionsMap.set(lowerOpt, opt);
                }
              });

              // Add current value if not already present (case-insensitive check)
              if (value && typeof value === "string" && value.trim() !== "") {
                const valueLower = value.toLowerCase();
                if (!optionsMap.has(valueLower)) {
                  optionsMap.set(valueLower, value);
                }
              }

              const allOptions = Array.from(optionsMap.values());
              allOptions.sort((a, b) => {
                const aIsStandard = dropdownOptions.includes(a);
                const bIsStandard = dropdownOptions.includes(b);
                if (aIsStandard && !bIsStandard) return -1;
                if (!aIsStandard && bIsStandard) return 1;
                return a.localeCompare(b);
              });
              
              return allOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ));
            })()}
          </CustomSelect>
        );
      }
      if (isDate) {
        return (
          <YmdDateInput
            name={fieldName}
            value={value}
            onChange={handleChange}
            className={inputClasses}
            disabled={!enabled}
            min={
              fieldName &&
              typeof fieldName === "string" &&
              fieldName.toLowerCase().includes("follow_up")
                ? getMinDate()
                : undefined
            }
          />
        );
      }
      // Format display value based on field type
      let displayValue = value;
      if (fieldName === "mrc" && !mrcFieldFocused && value) {
        displayValue = formatNumberWithCommas(value);
      } else if (
        fieldName === "circuit_id" &&
        !circuitIdFieldFocused &&
        value
      ) {
        const category = formData.category || "";
        if (
          category === "Mobility" ||
          category === "Analog" ||
          category === "Voice"
        ) {
          displayValue = formatPhoneNumber(value);
        }
      } else if (fieldName === "phone" && !phoneFieldFocused && value) {
        displayValue = formatPhoneNumber(value);
      }

      // Determine blur/focus handlers
      const handleBlur =
        fieldName === "mrc"
          ? handleMrcBlur
          : fieldName === "circuit_id" || fieldName === "phone"
          ? () => handlePhoneFieldBlur(fieldName)
          : undefined;

      const handleFocus =
        fieldName === "mrc"
          ? handleMrcFocus
          : fieldName === "circuit_id" || fieldName === "phone"
          ? () => handlePhoneFieldFocus(fieldName)
          : undefined;

      return (
        <input
          name={fieldName}
          type="text"
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          className={inputClasses}
          disabled={!enabled}
          placeholder={`Enter ${fieldLabel.toLowerCase()}...`}
        />
      );
    };

    if (isComparisonMode) {
      // 3-column layout: Field | Original Value | New Value
      // Use the originalValue that may have been set from modified_fields
      let originalDisplayValue = "";
      const displayOriginalValue =
        modifiedFieldData && modifiedFieldData.old_value !== undefined
          ? modifiedFieldData.old_value
          : originalValue;

      if (displayOriginalValue !== null && displayOriginalValue !== undefined) {
        if (isDate) {
          originalDisplayValue = formatDateForInput(displayOriginalValue);
        } else if (fieldName === "mrc" && displayOriginalValue) {
          // Show MRC without $ symbol in comparison mode original value
          originalDisplayValue = formatNumberWithCommas(displayOriginalValue);
        } else if (fieldName === "circuit_id" && displayOriginalValue) {
          // Format circuit_id as phone number for Mobility/Analog/Voice
          const category = formData.category || "";
          if (
            category === "Mobility" ||
            category === "Analog" ||
            category === "Voice"
          ) {
            originalDisplayValue = formatPhoneNumber(displayOriginalValue);
          } else {
            originalDisplayValue = String(displayOriginalValue).trim();
          }
        } else if (fieldName === "phone" && displayOriginalValue) {
          // Format phone field
          originalDisplayValue = formatPhoneNumber(displayOriginalValue);
        } else {
          originalDisplayValue = String(displayOriginalValue).trim();
        }
      }

      return (
        <React.Fragment key={fieldName}>
          <div className="text-xs font-medium text-gray-700 py-1 self-start">
            {fieldLabel}
          </div>
          <div></div>
          <div className="text-xs text-gray-600 bg-gray-50 p-1.5 rounded break-words border min-h-[2rem]">
            {originalDisplayValue || "-"}
          </div>
          <div>{renderInput()}</div>
        </React.Fragment>
      );
    }

    // 2-column layout: Label | Input (horizontal layout like HTML)
    return (
      <div
        key={fieldName}
        className={isNotesField ? "sm:col-span-2" : ""}
      >
        <label className="block text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] mb-1">
          {fieldLabel}
        </label>
        <div>{renderInput()}</div>
      </div>
    );
  };

  const SectionDivider = () => (
    <div
      className="border-t border-[var(--line,#e4e2dc)] mt-6 mb-4"
      style={isComparisonMode ? { gridColumn: "1 / -1" } : undefined}
      aria-hidden="true"
    />
  );

  const renderSectionHeading = (title) => (
    <div
      className="pb-3"
      style={isComparisonMode ? { gridColumn: "1 / -1" } : undefined}
    >
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--ink,#1c1c1a)]">
        {title}
      </h3>
    </div>
  );

  const renderStyledFieldList = (fieldNames) => (
    <div className={isComparisonMode ? undefined : "grid grid-cols-1 sm:grid-cols-3 gap-4"}>
      {renderFieldList(fieldNames)}
    </div>
  );

  const filterSectionFields = (fieldNames) =>
    fieldNames.filter(
      (fieldName) => !isImportMode || isFieldEnabled(fieldName)
    );

  const renderFieldList = (fieldNames) =>
    filterSectionFields(fieldNames).map((fieldName) => renderField(fieldName));

  const hasAddressSection =
    (item.address_id !== undefined && item.address_id !== null) ||
    formData.std_street_address ||
    formData.std_city ||
    formData.std_state ||
    formData.std_zip ||
    formData.std_country;

  const renderAddressSection = () => {
    if (!hasAddressSection) return null;

    return (
      <>
        <SectionDivider />
        {isComparisonMode ? (
          <>
            <div
              className="mt-2 pt-2 flex items-start justify-between gap-4"
              style={{ gridColumn: "1 / -1" }}
            >
              <h3 className="text-xs font-semibold text-gray-700 mb-1">
                Address Information
              </h3>
              {/* Use Standard Address card (comparison mode) — hidden for now
              {!tenantViewOnly &&
                standardizedAddressData &&
                !isStandardizing &&
                (standardizedAddressData.standard_address ||
                  standardizedAddressData.std_street_address ||
                  standardizedAddressData.std_city ||
                  standardizedAddressData.std_state ||
                  standardizedAddressData.std_zip) && (
                  <div className="flex-shrink-0">
                    <div className="p-1.5 bg-green-50 border border-green-200 rounded text-xs">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-green-700 text-xs break-words">
                            {standardizedAddressData.standard_address ||
                              (standardizedAddressData.std_street_address &&
                                [
                                  standardizedAddressData.std_street_address,
                                  standardizedAddressData.std_city,
                                  standardizedAddressData.std_state,
                                  standardizedAddressData.std_zip,
                                ]
                                  .filter(Boolean)
                                  .join(", ")) ||
                              "Address standardized"}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={handleUseStandard}
                          className="ml-2 px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 flex-shrink-0 transition-colors whitespace-nowrap"
                        >
                          Use Standard Address
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              {!tenantViewOnly && isStandardizing && (
                <div className="flex-shrink-0">
                  <div className="p-1.5 bg-yellow-50 border border-yellow-200 rounded text-xs">
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-yellow-600 mr-2"></div>
                      <span className="text-yellow-800 whitespace-nowrap">
                        Standardizing address...
                      </span>
                    </div>
                  </div>
                </div>
              )}
              */}
            </div>
            {ADDRESS_FIELDS.map((fieldName) => {
              const originalAddrValue = getOriginalAddressValue(fieldName);
              return (
                <React.Fragment key={fieldName}>
                  <div className="text-xs font-medium text-gray-700 py-1 self-start">
                    {getAddressFieldLabel(fieldName)}
                  </div>
                  <div></div>
                  <div className="text-xs text-gray-600 bg-gray-50 p-1.5 rounded break-words border min-h-[2rem]">
                    {originalAddrValue !== null &&
                    originalAddrValue !== undefined &&
                    originalAddrValue !== ""
                      ? originalAddrValue
                      : "-"}
                  </div>
                  <div>
                    <div className="w-full border rounded py-1 px-2 text-xs bg-gray-50 border-gray-300 text-gray-700 break-words min-h-[2rem]">
                      {formData[fieldName] || "-"}
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </>
        ) : (
          <>
            {renderSectionHeading("Address information")}
            {/* Use Standard Address card — hidden for now
            {!tenantViewOnly &&
              standardizedAddressData &&
              !isStandardizing &&
              (standardizedAddressData.standard_address ||
                standardizedAddressData.std_street_address ||
                standardizedAddressData.std_city ||
                standardizedAddressData.std_state ||
                standardizedAddressData.std_zip) && (
                <div className="mb-3">
                  <div className="p-2 bg-green-50 border border-green-200 rounded">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-green-700 text-sm break-words">
                          {standardizedAddressData.standard_address ||
                            (standardizedAddressData.std_street_address &&
                              [
                                standardizedAddressData.std_street_address,
                                standardizedAddressData.std_city,
                                standardizedAddressData.std_state,
                                standardizedAddressData.std_zip,
                              ]
                                .filter(Boolean)
                                .join(", ")) ||
                            "Address standardized"}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleUseStandard}
                        className="ml-2 px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 flex-shrink-0 transition-colors whitespace-nowrap"
                      >
                        Use Standard Address
                      </button>
                    </div>
                  </div>
                </div>
              )}
            {!tenantViewOnly && isStandardizing && (
              <div className="mb-3">
                <div className="p-2 bg-yellow-50 border border-yellow-200 rounded">
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600 mr-2"></div>
                    <span className="text-yellow-800 text-sm">
                      Standardizing address...
                    </span>
                  </div>
                </div>
              </div>
            )}
            */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {ADDRESS_FIELDS.map((fieldName) => {
              const addrValue = formData[fieldName] || "";
              // Detailed Inventory only: allow editing so a single circuit can move
              // to a new address (useAddressForRecordOnly). Import stays read-only.
              const canEditAddress = !tenantViewOnly && !isImportMode;
              return (
                <div key={fieldName}>
                  <label className="block text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] mb-1">
                    {getAddressFieldLabel(fieldName)}
                  </label>
                  {canEditAddress ? (
                    <input
                      name={fieldName}
                      type="text"
                      value={addrValue}
                      onChange={handleChange}
                      className="w-full rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-3 py-2 text-[13px] text-[var(--ink,#1c1c1a)] placeholder:text-[var(--faint,#9a9a95)] focus:outline-none focus:ring-2 focus:ring-[var(--accent,#a51c1c)]/25 focus:border-[var(--accent,#a51c1c)]"
                      placeholder={`Enter ${getAddressFieldLabel(fieldName).toLowerCase()}...`}
                    />
                  ) : (
                    <div className="w-full rounded-md border border-[var(--line-strong,#d5d3cc)] bg-[var(--sunken,#eeece7)] px-3 py-2 text-[13px] text-[var(--ink,#1c1c1a)]">
                      {addrValue || "-"}
                    </div>
                  )}
                </div>
              );
            })}
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-5 bg-[#1c1c1a]/25 backdrop-blur-[4px] overflow-hidden"
      style={{ WebkitBackdropFilter: "blur(4px)" }}
      role="presentation"
    >
      <Toast
        title={disconnectToast.title}
        message={disconnectToast.message}
        type={disconnectToast.type}
        duration={5000}
        onClose={clearDisconnectToast}
      />
      <div
        role="dialog"
        aria-modal="true"
        className={`relative z-[1] flex max-h-[92vh] w-full flex-col overflow-hidden rounded-[12px] bg-white shadow-[0_24px_80px_rgba(28,28,26,0.28)] ${
          isComparisonMode ? "max-w-[1100px]" : "max-w-[920px]"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-[var(--line,#e4e2dc)] px-6 py-4 flex-shrink-0">
          <div className="min-w-0">
            <p className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[#a51c1c]">
              {modalEyebrow}
            </p>
            <h2 className="mt-1 truncate text-[22px] font-semibold leading-tight text-[var(--ink,#1c1c1a)]">
              {modalCircuitTitle}
            </h2>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {(formData.provider || item.provider) ? (
                <span className="inline-flex items-center rounded-full bg-[var(--sunken,#eeece7)] px-2 py-0.5 text-[12px] font-medium text-[var(--ink,#1c1c1a)]">
                  {formData.provider || item.provider}
                </span>
              ) : null}
              {modalStatus ? (
                <span className={`inline-flex items-center text-[13px] font-medium ${modalStatusClass}`}>
                  {modalStatus}
                </span>
              ) : null}
            </div>
            {modalAddress && modalAddress !== "-" ? (
              <p className="mt-1 text-[13px] text-[var(--muted,#6b6a66)] truncate">
                {modalAddress}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-md p-1 text-[26px] leading-none text-[var(--muted,#6b6a66)] hover:bg-[var(--sunken,#eeece7)] hover:text-[var(--ink,#1c1c1a)]"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div
            className="px-6 py-5 flex-1 overflow-y-auto overflow-x-hidden min-h-0 overscroll-contain"
          >
            {isLoadingMapping ? (
              <div className="col-span-2 p-4 text-center text-gray-500 text-sm">
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                  <span>Loading field configuration...</span>
                </div>
              </div>
            ) : (
              <div
                className={
                  isComparisonMode
                    ? "grid gap-y-1.5 items-center"
                    : "space-y-0"
                }
                style={
                  isComparisonMode
                    ? {
                        gridTemplateColumns: "minmax(120px, auto) 0 1fr 1fr",
                        gap: "6px 16px",
                      }
                    : {}
                }
              >
                {/* Comparison Mode Header - Show above regular fields */}
                {isComparisonMode && (
                  <>
                    <div className="font-bold text-gray-500 text-xs pb-2">
                      Field
                    </div>
                    <div></div>
                    <div className="font-bold text-gray-500 text-xs pb-2">
                      Original Value
                    </div>
                    <div className="font-bold text-gray-500 text-xs pb-2">
                      New Value
                    </div>
                  </>
                )}

                {/* Regular fields — section-wise (design reference) */}
                {!isComparisonMode && (
                  <>
                    {linkedDisconnect && !isImportMode ? (
                      <>
                        {renderSectionHeading("Disconnect history")}
                        <div className="mb-6 rounded-[10px] border border-[var(--line,#e4e2dc)] bg-[var(--sunken,#eeece7)]/40 px-4 py-3">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-[14px] font-semibold text-[var(--ink,#1c1c1a)]">
                                  {linkedDisconnect.request_number}
                                </span>
                                <span className="inline-flex items-center rounded-full border border-sky-200 bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-900">
                                  {statusLabel(linkedDisconnect.status)}
                                </span>
                              </div>
                              {(linkedDisconnect.circuit_count || 0) > 1 ? (
                                <p className="mt-0.5 text-[12px] text-[var(--muted,#6b6a66)]">
                                  with {(linkedDisconnect.circuit_count || 1) - 1}{" "}
                                  other circuit
                                  {(linkedDisconnect.circuit_count || 1) - 1 === 1
                                    ? ""
                                    : "s"}
                                </p>
                              ) : null}
                            </div>
                            <button
                              type="button"
                              onClick={() => setShowDisconnectViewModal(true)}
                              className="text-[13px] font-medium text-[var(--accent,#a51c1c)] hover:underline"
                            >
                              Open request
                            </button>
                          </div>
                          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-[12px]">
                            <div>
                              <p className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                                Submitted
                              </p>
                              <p className="mt-0.5 text-[var(--ink,#1c1c1a)]">
                                {linkedDisconnect.supplier_groups?.[0]
                                  ?.date_submitted_to_supplier
                                  ? String(
                                      linkedDisconnect.supplier_groups[0]
                                        .date_submitted_to_supplier
                                    ).slice(0, 10)
                                  : linkedDisconnect.created_at
                                    ? String(linkedDisconnect.created_at).slice(
                                        0,
                                        10
                                      )
                                    : "—"}
                              </p>
                            </div>
                            <div>
                              <p className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                                Tracking number
                              </p>
                              <p className="mt-0.5 text-[var(--ink,#1c1c1a)]">
                                {linkedDisconnect.supplier_groups
                                  ?.map((g) => g.tracking_number)
                                  .filter(Boolean)
                                  .join(", ") || "—"}
                              </p>
                            </div>
                            <div>
                              <p className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                                Confirmed
                              </p>
                              <p className="mt-0.5 text-[var(--ink,#1c1c1a)]">
                                {linkedDisconnect.supplier_groups?.[0]
                                  ?.supplier_confirmed_disconnect_date
                                  ? String(
                                      linkedDisconnect.supplier_groups[0]
                                        .supplier_confirmed_disconnect_date
                                    ).slice(0, 10)
                                  : "—"}
                              </p>
                            </div>
                            <div>
                              <p className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                                Evidence
                              </p>
                              <p className="mt-0.5 text-[var(--ink,#1c1c1a)]">
                                {(linkedDisconnect.evidence || []).length > 0
                                  ? `${linkedDisconnect.evidence.length} file${
                                      linkedDisconnect.evidence.length === 1
                                        ? ""
                                        : "s"
                                    }`
                                  : "None"}
                              </p>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : null}

                    {renderSectionHeading("Service")}
                    {renderStyledFieldList(SERVICE_DETAIL_FIELDS)}

                    {filterSectionFields(NOTES_FOLLOWUP_FIELDS).length > 0 && (
                      <>
                        <div className="mt-6" />
                        {renderSectionHeading("Notes and follow up")}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div className="sm:col-span-2">
                            {filterSectionFields(["notes"]).map((f) =>
                              renderField(f)
                            )}
                          </div>
                          <div className="space-y-4">
                            {filterSectionFields(["follow_up_date", "action"]).map(
                              (f) => renderField(f)
                            )}
                          </div>
                        </div>
                      </>
                    )}

                    {filterSectionFields(TECHNICAL_CONTACT_FIELDS).length > 0 && (
                      <>
                        <div className="mt-6" />
                        {renderSectionHeading("Technical details")}
                        {renderStyledFieldList(TECHNICAL_CONTACT_FIELDS)}
                      </>
                    )}
                  </>
                )}

                {/* Comparison mode — service fields then notes, technical, address */}
                {isComparisonMode && (
                  <>
                    {renderFieldList(SERVICE_DETAIL_FIELDS)}
                    {filterSectionFields(NOTES_FOLLOWUP_FIELDS).length > 0 && (
                      <>
                        <SectionDivider />
                        {renderSectionHeading("Notes & Follow-up")}
                        {renderFieldList(NOTES_FOLLOWUP_FIELDS)}
                      </>
                    )}
                    {filterSectionFields(TECHNICAL_CONTACT_FIELDS).length > 0 && (
                      <>
                        <SectionDivider />
                        {renderSectionHeading("Technical & Contact Details")}
                        {renderFieldList(TECHNICAL_CONTACT_FIELDS)}
                      </>
                    )}
                  </>
                )}

                {renderAddressSection()}

                {/* Verification Section - Import mode only */}
                {isImportMode && (
                  <div
                    style={isComparisonMode ? { gridColumn: "1 / -1" } : {}}
                  >
                    <SectionDivider />
                    {renderSectionHeading("Verification")}
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="verified"
                        name="verified"
                        checked={formData.verified || false}
                        disabled={tenantViewOnly}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            verified: e.target.checked,
                          }))
                        }
                        className={`h-4 w-4 text-[var(--accent,#a51c1c)] focus:ring-[var(--accent,#a51c1c)] border-gray-300 rounded ${
                          isComparisonMode &&
                          originalItem &&
                          originalItem.verified !== formData.verified
                            ? "ring-2 ring-yellow-400"
                            : ""
                        }`}
                      />
                      <label
                        htmlFor="verified"
                        className="ml-2 block text-sm font-medium text-gray-700"
                      >
                        Verified
                      </label>
                    </div>
                    {isComparisonMode && originalItem && (
                      <div className="mt-1 text-sm text-gray-500">
                        Original:{" "}
                        {originalItem.verified ? "✓ Verified" : "Not Verified"}
                      </div>
                    )}
                  </div>
                )}

                {/* Record Information */}
                {item && isComparisonMode && (
                  <div
                    className="mt-0 pt-0"
                    style={{ gridColumn: "1 / -1" }}
                  >
                    <SectionDivider />
                    {renderSectionHeading("Record Information")}
                    <>
                      <div className="text-xs font-medium text-gray-700 py-1">
                        Updated At
                      </div>
                      <div></div>
                      <div className="text-xs text-gray-600 bg-gray-50 p-1.5 rounded break-words border min-h-[2rem]">
                        {item.updated_at
                          ? formatDateOnly(item.updated_at)
                          : "-"}
                      </div>
                      <div></div>
                    </>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-shrink-0 items-center justify-between gap-3 border-t border-[var(--line,#e4e2dc)] px-6 py-3">
            <div className="min-w-0 flex items-center gap-3">
              {!isImportMode &&
                !isNewRecord &&
                item?.id &&
                linkedDisconnect && (
                  <button
                    type="button"
                    onClick={() => setShowDisconnectViewModal(true)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-[var(--crit,#c0262b)] bg-white px-3 py-2 text-[13px] font-medium text-[var(--crit,#c0262b)] hover:bg-[var(--crit-soft,#fbe6e6)]"
                  >
                    <Eye className="h-4 w-4" strokeWidth={2} aria-hidden />
                    View disconnect {linkedDisconnect.request_number}
                  </button>
                )}
              {!tenantViewOnly &&
                !isImportMode &&
                !isNewRecord &&
                item?.id &&
                !linkedDisconnect &&
                (formData.provider || item.provider || provider) && (
                  <button
                    type="button"
                    onClick={() => setShowDisconnectCreateModal(true)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-3 py-2 text-[13px] font-medium text-[var(--crit,#c0262b)] hover:bg-[var(--crit-soft,#fbe6e6)]"
                  >
                    <Power className="h-4 w-4" strokeWidth={2} aria-hidden />
                    Disconnect
                  </button>
                )}
              <p className="min-w-0 text-[12px] text-[var(--muted,#6b6a66)] truncate">
                {updatedAtLabel
                  ? `Last updated${updatedBy ? ` by ${updatedBy}` : ""} on ${updatedAtLabel}`
                  : ""}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-4 py-2 text-[13px] font-medium text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)]"
              >
                {tenantViewOnly ? "Close" : "Cancel"}
              </button>
              {!tenantViewOnly && (
                <button
                  type="submit"
                  disabled={!hasChanges}
                  className={`inline-flex items-center justify-center rounded-md px-4 py-2 text-[13px] font-medium text-white ${
                    hasChanges
                      ? "bg-[var(--accent,#a51c1c)] hover:opacity-90 cursor-pointer"
                      : "bg-gray-300 cursor-not-allowed opacity-50"
                  }`}
                >
                  Save changes
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
      {showDisconnectCreateModal && (
        <DisconnectRequestModal
          item={{
            ...item,
            ...formData,
            id: item.id,
            provider: formData.provider || item.provider || provider,
          }}
          tenantId={tenantId}
          onClose={() => setShowDisconnectCreateModal(false)}
          onSubmitted={(data) => {
            setShowDisconnectCreateModal(false);
            if (data) setLinkedDisconnect(data);
          }}
        />
      )}
      {showDisconnectViewModal && linkedDisconnect && (
        <DisconnectRequestDetailModal
          requestId={linkedDisconnect.id}
          initialData={linkedDisconnect}
          tenantId={tenantId}
          readOnly={tenantViewOnly}
          onClose={() => setShowDisconnectViewModal(false)}
          onSaved={(data) => {
            if (data) setLinkedDisconnect(data);
            // Set toast immediately so it is visible after the detail modal closes
            setDisconnectToast({
              title: "Saved",
              message: "Disconnect request changes saved successfully.",
              type: "success",
            });
          }}
        />
      )}
    </div>
  );
};

export default EditModal;
